/**
 * Who decided the payer this check ran against (#314 AC8).
 *
 * The "Suggested by VitalRev AI" badge answers "why does this row show a payer
 * the source file never said?" and it used to be inferred purely from the names
 * differing. That inference was safe only while the AI was the ONLY thing that
 * could make them differ. A staff correction now can too — that is the whole
 * point of editing a check in place — and `requestPayerName` is deliberately
 * preserved as the raw shorthand across a correction, so a human's edit
 * produces exactly the shape the badge reads as machine work.
 *
 * Crediting a person's decision to the AI is not a cosmetic error: staff judge
 * how much to trust a resolved payer by who resolved it, and the sparkle is the
 * signal that says "check this one".
 *
 * Pure, and deliberately mode-first: the mode is a recorded fact about how the
 * check was created, the name comparison is only an inference.
 */
export type PayerProvenance = 'ai' | 'staff' | null

/** The check's `mode` for a correction a human typed. Mirrors the backend's
 *  STAFF_CORRECTION_MODE — a sentinel written at check creation. */
export const STAFF_CORRECTION_MODE = 'staff_correction'
/** The alias-learning sweep's own rechecks. */
export const ALIAS_PROBE_MODE = 'alias_probe'

export function payerProvenance(input: {
  mode?: string | null
  /** Resolved directory name the check ran against. */
  resolvedPayer?: string | null
  /** Raw shorthand as it arrived (requestPayerName / the appointment's payer). */
  rawPayer?: string | null
}): PayerProvenance {
  if (input.mode === STAFF_CORRECTION_MODE) return 'staff'
  // An alias-probe recheck IS AI-resolved even when it stores payerName == the
  // shorthand, so the name comparison below can't see it.
  if (input.mode === ALIAS_PROBE_MODE) return 'ai'
  const resolved = (input.resolvedPayer ?? '').trim()
  const raw = (input.rawPayer ?? '').trim()
  if (!resolved || !raw) return null
  return resolved.toLowerCase() !== raw.toLowerCase() ? 'ai' : null
}
